import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { crApi } from "../lib/crScope";
import {
  Plus, Edit, Trash2, FileText, CheckSquare, List,
  Save, X, GripVertical, ChevronDown, ChevronRight, MessageSquare, Copy, RotateCcw, Layers
} from "lucide-react";

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
function SortableItem({ item, idx, editingItemId, setEditingItemId, handleUpdateItem, handleDeleteItem, canEdit }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const [showObs, setShowObs] = useState(false);
  const [obsValue, setObsValue] = useState(item.observacion || "");

  // Mantener el valor local sincronizado si el item cambia (refetch de plantilla)
  useEffect(() => { setObsValue(item.observacion || ""); }, [item.observacion]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const saveObservacion = (value) => {
    handleUpdateItem(item.id, { ...item, observacion: value });
    setShowObs(false);
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className={`flex flex-col gap-2 p-2 bg-white dark:bg-slate-800 rounded border hover:shadow-sm ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''}`}
    >
      <div className="flex items-center gap-2">
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none"
      >
        <GripVertical className="w-4 h-4 text-slate-400 hover:text-slate-600" />
      </div>
      <span className="text-sm text-slate-500 w-6">{idx + 1}.</span>
      {editingItemId === item.id ? (
        <div className="flex-1 flex gap-2">
          <Input
            defaultValue={item.texto}
            id={`edit-item-${item.id}`}
            className="flex-1"
          />
          <Button size="sm" onClick={() => {
            const input = document.getElementById(`edit-item-${item.id}`);
            handleUpdateItem(item.id, { ...item, texto: input.value });
          }}>
            <Save className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditingItemId(null)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <>
          <div className="flex-1 flex items-center gap-2 flex-wrap">
            <span className="text-sm">{item.texto}</span>
            {item.tipo_cobertura && (
              <Badge variant="outline" className={
                item.tipo_cobertura === "Daños Propios" || item.tipo_cobertura === "Formato Americano"
                  ? "bg-purple-100 text-purple-700 text-xs" 
                  : "bg-orange-100 text-orange-700 text-xs"
              }>
                {item.tipo_cobertura}
              </Badge>
            )}
            {item.observacion && (
              <Badge
                variant="outline"
                className="bg-amber-100 text-amber-800 border-amber-300 text-xs gap-1"
                title={item.observacion}
                data-testid={`item-observacion-badge-${item.id}`}
              >
                <MessageSquare className="w-3 h-3" /> {item.observacion}
              </Badge>
            )}
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <label
                className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer select-none"
                title="Agregar una observación/condición a esta cobertura"
              >
                <Checkbox
                  checked={showObs || !!item.observacion}
                  onCheckedChange={(v) => {
                    setShowObs(!!v);
                    if (!v && item.observacion) saveObservacion("");
                  }}
                  data-testid={`item-observacion-check-${item.id}`}
                />
                Observación
              </label>
              <Button size="icon" variant="ghost" onClick={() => setEditingItemId(item.id)}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDeleteItem(item.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}
      </div>

      {/* Campo de observación (aparece al activar el check) */}
      {canEdit && editingItemId !== item.id && (showObs || (item.observacion && showObs)) && (
        <div className="flex gap-2 items-start pl-8" data-testid={`item-observacion-editor-${item.id}`}>
          <Input
            value={obsValue}
            onChange={(e) => setObsValue(e.target.value)}
            placeholder="Ej: Aplica si tiene existencia"
            className="flex-1 text-sm"
          />
          <Button size="sm" onClick={() => saveObservacion(obsValue.trim())} data-testid={`item-observacion-save-${item.id}`}>
            <Save className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function PlantillasChecklist({ API_URL, getAuthHeaders, canEdit }) {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ramosDisponibles, setRamosDisponibles] = useState({ ramos_sin_plantilla: [], todos_ramos: [] });
  
  // Dialog states
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [cloneForm, setCloneForm] = useState({ nombre: "", ramo: "", descripcion: "" });
  const [showItemsDialog, setShowItemsDialog] = useState(false);
  const [selectedPlantilla, setSelectedPlantilla] = useState(null);
  
  // Version history
  const [showHistorialDialog, setShowHistorialDialog] = useState(false);
  const [historialVersiones, setHistorialVersiones] = useState([]);
  const [historialActual, setHistorialActual] = useState(null);
  const [compareSelection, setCompareSelection] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [showPapeleraDialog, setShowPapeleraDialog] = useState(false);
  const [papelera, setPapelera] = useState([]);
  const [loadingPapelera, setLoadingPapelera] = useState(false);
  const [notaVersion, setNotaVersion] = useState("");
  const [historialLoading, setHistorialLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    nombre: "",
    ramo: "",
    ramos: [],
    descripcion: "",
    items: []
  });
  
  // New item form
  const [newItem, setNewItem] = useState({ texto: "", categoria: "", tipo_cobertura: "" });

  // Formatos (subplantillas)
  const [showFormatosDialog, setShowFormatosDialog] = useState(false);
  const [formatosPlantilla, setFormatosPlantilla] = useState(null);
  const [newFormato, setNewFormato] = useState("");
  const [renamingFormato, setRenamingFormato] = useState(null); // { anterior, nombre }
  const [savingFormato, setSavingFormato] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  
  // Draft de items (los cambios se guardan todos juntos con un solo botón)
  const [draftItems, setDraftItems] = useState([]);
  const [itemsDirty, setItemsDirty] = useState(false);
  const [savingItems, setSavingItems] = useState(false);
  
  // Categories for grouping items
  const categorias = [
    "Datos Generales",
    "Coberturas",
    "Deducibles",
    "Cláusulas y Endosos",
    "Otros"
  ];

  // Tipo de cobertura options por ramo
  const getTipoCoberturaOpciones = (ramo) => {
    const ramoUpper = (ramo || "").toUpperCase();
    if (ramoUpper.includes("VEHICULO") && ramoUpper.includes("MOTOR")) {
      return ["Daños Propios", "Daños a Tercero"];
    }
    if (ramoUpper.includes("FIDELIDAD") && ramoUpper.includes("3D")) {
      return ["Formato Americano", "Formato Inglés"];
    }
    return [];
  };

  // Check if ramo requires tipo_cobertura
  const ramoRequiereTipoCobertura = (ramo) => {
    const ramoUpper = (ramo || "").toUpperCase();
    return (ramoUpper.includes("VEHICULO") && ramoUpper.includes("MOTOR")) ||
           (ramoUpper.includes("FIDELIDAD") && ramoUpper.includes("3D"));
  };

  // Get label for tipo_cobertura based on plantilla/ramo
  const getTipoCoberturaLabel = (plantilla) => {
    if (plantilla?.formatos_personalizados) return "Formato";
    const ramoUpper = (plantilla?.ramo || "").toUpperCase();
    if (ramoUpper.includes("VEHICULO") && ramoUpper.includes("MOTOR")) {
      return "Tipo Cobertura";
    }
    if (ramoUpper.includes("FIDELIDAD") && ramoUpper.includes("3D")) {
      return "Formato";
    }
    return "Tipo";
  };

  // ====== Formatos (subplantillas) ======
  const openFormatosDialog = (plantilla) => {
    setFormatosPlantilla(plantilla);
    setNewFormato("");
    setRenamingFormato(null);
    setShowFormatosDialog(true);
  };

  const formatosApi = (plantillaId) => `${API_URL}${crApi()}/checklist-renovaciones/plantillas/${plantillaId}/formatos`;

  const handleCrearFormato = async () => {
    const nombre = newFormato.trim();
    if (!nombre) { toast.error("Escribe el nombre del formato"); return; }
    setSavingFormato(true);
    try {
      const res = await fetch(formatosApi(formatosPlantilla.id), {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ nombre })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al crear el formato");
      toast.success(`Formato "${nombre}" creado`);
      setNewFormato("");
      setFormatosPlantilla(data);
      fetchPlantillas();
    } catch (e) { toast.error(e.message); } finally { setSavingFormato(false); }
  };

  const handleRenombrarFormato = async () => {
    const nombre = (renamingFormato?.nombre || "").trim();
    if (!nombre) { toast.error("Escribe el nuevo nombre"); return; }
    setSavingFormato(true);
    try {
      const res = await fetch(formatosApi(formatosPlantilla.id), {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ anterior: renamingFormato.anterior, nombre })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al renombrar el formato");
      toast.success("Formato renombrado");
      setRenamingFormato(null);
      setFormatosPlantilla(data);
      fetchPlantillas();
    } catch (e) { toast.error(e.message); } finally { setSavingFormato(false); }
  };

  const handleEliminarFormato = async (nombre) => {
    if (!window.confirm(`¿Eliminar el formato "${nombre}"?`)) return;
    setSavingFormato(true);
    try {
      const res = await fetch(`${formatosApi(formatosPlantilla.id)}/${encodeURIComponent(nombre)}`, {
        method: "DELETE",
        headers: getAuthHeaders().headers
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al eliminar el formato");
      toast.success("Formato eliminado");
      setFormatosPlantilla(data);
      fetchPlantillas();
    } catch (e) { toast.error(e.message); } finally { setSavingFormato(false); }
  };

  const fetchPlantillas = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/plantillas`, getAuthHeaders());
      const data = await res.json();
      setPlantillas(data.plantillas || []);
    } catch (error) {
      console.error("Error fetching plantillas:", error);
      toast.error("Error al cargar plantillas");
    }
  }, [API_URL, getAuthHeaders]);

  const fetchRamosDisponibles = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/ramos-disponibles`, getAuthHeaders());
      const data = await res.json();
      setRamosDisponibles(data);
    } catch (error) {
      console.error("Error fetching ramos:", error);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPlantillas(), fetchRamosDisponibles()]);
      // Check if user is admin
      try {
        const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/user-tipo`, getAuthHeaders());
        if (res.ok) {
          const data = await res.json();
          setIsAdmin(data.is_admin || false);
        }
      } catch {}
      setLoading(false);
    };
    loadData();
  }, [fetchPlantillas, fetchRamosDisponibles]);

  const fetchHistorialVersiones = async (plantillaId) => {
    setHistorialLoading(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/plantillas/${plantillaId}/historial-versiones`, getAuthHeaders());
      if (res.ok) {
        const data = await res.json();
        setHistorialVersiones(data.historial || []);
        setHistorialActual(data.actual || null);
        setCompareSelection([]);
      } else {
        toast.error("No tiene permisos para ver el historial");
        setHistorialVersiones([]);
        setHistorialActual(null);
      }
    } catch {
      toast.error("Error al cargar historial");
    } finally {
      setHistorialLoading(false);
    }
  };

  const toggleCompareSelection = (id) => {
    setCompareSelection(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : (prev.length >= 2 ? [prev[1], id] : [...prev, id])
    );
  };

  const handleCompararVersiones = async () => {
    if (compareSelection.length !== 2 || !selectedPlantilla) return;
    setComparing(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/plantillas/${selectedPlantilla.id}/comparar-versiones`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ version_id_a: compareSelection[0], version_id_b: compareSelection[1] })
      });
      const data = await res.json();
      if (res.ok) {
        setCompareData(data);
        setShowCompareDialog(true);
      } else {
        toast.error(data.detail || "Error al comparar versiones");
      }
    } catch {
      toast.error("Error de conexión al comparar");
    } finally {
      setComparing(false);
    }
  };

  const handleRestoreVersion = async (versionId) => {
    if (!selectedPlantilla) return;
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/plantillas/${selectedPlantilla.id}/restaurar-version`, {
        ...getAuthHeaders(),
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ version_id: versionId })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Versión restaurada");
        setShowHistorialDialog(false);
        fetchPlantillas();
      } else {
        toast.error(data.detail || "Error al restaurar");
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  const openCloneDialog = (plantilla) => {
    setCloneSource(plantilla);
    setCloneForm({ nombre: `${plantilla.nombre} (Copia)`, ramo: "", descripcion: plantilla.descripcion || "" });
    setShowCloneDialog(true);
  };

  const handleClonarPlantilla = async () => {
    if (!cloneForm.ramo) {
      toast.error("Seleccione el ramo de la nueva plantilla");
      return;
    }
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/plantillas/${cloneSource.id}/clonar`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(cloneForm)
      });
      if (res.ok) {
        toast.success("Plantilla clonada con todos sus items");
        setShowCloneDialog(false);
        fetchPlantillas();
        fetchRamosDisponibles();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al clonar plantilla");
      }
    } catch (e) {
      toast.error("Error al clonar plantilla");
    }
  };

  const handleCreatePlantilla = async () => {
    if (!formData.nombre || formData.ramos.length === 0) {
      toast.error("Nombre y al menos un Ramo son requeridos");
      return;
    }

    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/plantillas`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, ramo: formData.ramos[0] })
      });

      if (res.ok) {
        toast.success("Plantilla creada exitosamente");
        setShowNewDialog(false);
        resetForm();
        fetchPlantillas();
        fetchRamosDisponibles();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al crear plantilla");
      }
    } catch (error) {
      toast.error("Error al crear plantilla");
    }
  };

  const handleUpdatePlantilla = async () => {
    if (!selectedPlantilla) return;
    if (formData.ramos.length === 0) {
      toast.error("Debe seleccionar al menos un ramo");
      return;
    }

    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/plantillas/${selectedPlantilla.id}`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: formData.nombre,
          descripcion: formData.descripcion,
          ramos: formData.ramos
        })
      });

      if (res.ok) {
        toast.success("Plantilla actualizada");
        setShowEditDialog(false);
        fetchPlantillas();
        fetchRamosDisponibles();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Error al actualizar");
      }
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleDeletePlantilla = async (plantilla) => {
    if (!window.confirm(`¿Eliminar la plantilla "${plantilla.nombre}"? Irá a la papelera y podrás recuperarla durante 30 días.`)) return;

    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/plantillas/${plantilla.id}`, {
        method: "DELETE",
        ...getAuthHeaders()
      });

      if (res.ok) {
        toast.success("Plantilla movida a la papelera (recuperable 30 días)");
        fetchPlantillas();
        fetchRamosDisponibles();
      } else {
        toast.error("Error al eliminar");
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const fetchPapelera = async () => {
    setLoadingPapelera(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/plantillas-papelera`, getAuthHeaders());
      if (res.ok) {
        const data = await res.json();
        setPapelera(data.plantillas || []);
      }
    } catch (error) {
      toast.error("Error al cargar la papelera");
    }
    setLoadingPapelera(false);
  };

  const handleRestaurarPapelera = async (plantilla) => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/plantillas-papelera/${plantilla.id}/restaurar`, {
        method: "POST",
        ...getAuthHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(data.message || "Plantilla restaurada");
        fetchPapelera();
        fetchPlantillas();
        fetchRamosDisponibles();
      } else {
        toast.error(data.detail || "Error al restaurar");
      }
    } catch (error) {
      toast.error("Error al restaurar");
    }
  };

  const handleAddItem = () => {
    if (!newItem.texto || !selectedPlantilla) {
      toast.error("El texto del item es requerido");
      return;
    }

    // Validate tipo_cobertura for VEHICULO DE MOTOR
    if (selectedPlantilla.tiene_tipo_cobertura && !newItem.tipo_cobertura) {
      toast.error(selectedPlantilla?.formatos_personalizados
        ? "Debe seleccionar el formato"
        : "Debe seleccionar el tipo de cobertura (Daños Propios o Daños a Tercero)");
      return;
    }

    const itemData = {
      id: `nuevo-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
      texto: newItem.texto,
      categoria: newItem.categoria || null,
      observacion: null
    };
    if (selectedPlantilla.tiene_tipo_cobertura) {
      itemData.tipo_cobertura = newItem.tipo_cobertura;
    }

    setDraftItems((prev) => [...prev, itemData]);
    setItemsDirty(true);
    setNewItem({ texto: "", categoria: "", tipo_cobertura: "" });
  };

  const handleUpdateItem = (itemId, itemData) => {
    setDraftItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, ...itemData } : it)));
    setItemsDirty(true);
    setEditingItemId(null);
  };

  const handleDeleteItem = (itemId) => {
    if (!window.confirm("¿Eliminar este item?")) return;
    setDraftItems((prev) => prev.filter((it) => it.id !== itemId));
    setItemsDirty(true);
  };

  const handleGuardarCambiosItems = async () => {
    if (!selectedPlantilla) return;
    setSavingItems(true);
    try {
      const payload = {
        items: draftItems.map((it) => ({
          id: it.id && String(it.id).startsWith("nuevo-") ? null : it.id,
          texto: it.texto,
          categoria: it.categoria || null,
          observacion: it.observacion || null,
          tipo_cobertura: it.tipo_cobertura || null
        })),
        nota: notaVersion || null
      };
      const res = await fetch(`${API_URL}${crApi()}/checklist-renovaciones/plantillas/${selectedPlantilla.id}/items/bulk`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok) {
        if (data.cambios) {
          toast.success(`Cambios guardados. Nueva versión: V ${data.plantilla?.version}`);
        } else {
          toast.info("No hay cambios que guardar. La versión se mantiene.");
        }
        setItemsDirty(false);
        setNotaVersion("");
        setShowItemsDialog(false);
        setEditingItemId(null);
        fetchPlantillas();
      } else {
        toast.error(data.detail || "Error al guardar cambios");
      }
    } catch {
      toast.error("Error de conexión al guardar");
    } finally {
      setSavingItems(false);
    }
  };

  // DnD Kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for reordering items (local, se guarda con el botón Guardar Cambios)
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = draftItems.findIndex((item) => item.id === active.id);
    const newIndex = draftItems.findIndex((item) => item.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    setDraftItems(arrayMove(draftItems, oldIndex, newIndex));
    setItemsDirty(true);
  };

  const resetForm = () => {
    setFormData({ nombre: "", ramo: "", ramos: [], descripcion: "", items: [] });
    setSelectedPlantilla(null);
    setNewItem({ texto: "", categoria: "" });
  };

  const toggleRamoForm = (ramo) => {
    setFormData(prev => ({
      ...prev,
      ramos: prev.ramos.includes(ramo) ? prev.ramos.filter(r => r !== ramo) : [...prev.ramos, ramo]
    }));
  };

  const ramosDePlantilla = (plantilla) =>
    (plantilla?.ramos && plantilla.ramos.length ? plantilla.ramos : (plantilla?.ramo ? [plantilla.ramo] : []));

  const openEditDialog = (plantilla) => {
    setSelectedPlantilla(plantilla);
    setFormData({
      nombre: plantilla.nombre,
      ramo: plantilla.ramo,
      ramos: ramosDePlantilla(plantilla),
      descripcion: plantilla.descripcion || "",
      items: plantilla.items || []
    });
    setShowEditDialog(true);
  };

  const openItemsDialog = (plantilla) => {
    setSelectedPlantilla(plantilla);
    setDraftItems(plantilla.items ? plantilla.items.map((it) => ({ ...it })) : []);
    setItemsDirty(false);
    setEditingItemId(null);
    setNewItem({ texto: "", categoria: "", tipo_cobertura: "" });
    setShowItemsDialog(true);
  };

  const handleItemsDialogChange = (open) => {
    if (!open && itemsDirty) {
      if (!window.confirm("Tiene cambios sin guardar. ¿Cerrar sin guardar los cambios?")) return;
      setItemsDirty(false);
    }
    setShowItemsDialog(open);
    if (!open) setEditingItemId(null);
  };

  // Group items by category
  const groupItemsByCategory = (items) => {
    const grouped = {};
    items?.forEach(item => {
      const cat = item.categoria || "Sin Categoría";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Plantillas de Checklist</h2>
          <p className="text-sm text-slate-500">Gestiona las plantillas de evaluación por ramo</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="outline" onClick={() => { setShowPapeleraDialog(true); fetchPapelera(); }} data-testid="papelera-plantillas-btn">
                <Trash2 className="w-4 h-4 mr-2 text-slate-500" />
                Papelera
              </Button>
            )}
            <Button onClick={() => { resetForm(); setShowNewDialog(true); }} data-testid="new-plantilla-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Plantilla
            </Button>
          </div>
        )}
      </div>

      {/* Plantillas Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plantillas.map((plantilla) => (
          <Card key={plantilla.id} className="hover:shadow-lg transition-shadow" data-testid={`plantilla-card-${plantilla.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">{plantilla.nombre}</CardTitle>
                  <div className="flex items-center flex-wrap gap-2 mt-1">
                    {(plantilla.ramos && plantilla.ramos.length ? plantilla.ramos : [plantilla.ramo]).map((r) => (
                      <Badge key={r} variant="outline" className="bg-blue-50 text-blue-700">
                        {r}
                      </Badge>
                    ))}
                    <Badge variant="secondary" className="bg-slate-100 text-slate-700 font-mono text-xs" data-testid={`version-badge-${plantilla.id}`}>
                      V {plantilla.version || "1.0"}
                    </Badge>
                  </div>
                </div>
                <Badge className={plantilla.activa ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}>
                  {plantilla.activa ? "Activa" : "Inactiva"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {plantilla.descripcion && (
                <p className="text-sm text-slate-500 mb-3">{plantilla.descripcion}</p>
              )}
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
                <CheckSquare className="w-4 h-4" />
                <span>{plantilla.items?.length || 0} items de evaluación</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => openItemsDialog(plantilla)}>
                  <List className="w-4 h-4 mr-1" />
                  Ver Items
                </Button>
                {isAdmin && (
                  <Button size="sm" variant="outline" className="text-indigo-600 border-indigo-200" onClick={() => { setSelectedPlantilla(plantilla); fetchHistorialVersiones(plantilla.id); setShowHistorialDialog(true); }} data-testid={`historial-btn-${plantilla.id}`}>
                    <FileText className="w-4 h-4 mr-1" />
                    Historial
                  </Button>
                )}
                {canEdit && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => openEditDialog(plantilla)}>
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button size="sm" variant="outline" className="text-teal-600 border-teal-200" onClick={() => openCloneDialog(plantilla)} data-testid={`clonar-plantilla-${plantilla.id}`}>
                      <Copy className="w-4 h-4 mr-1" />
                      Clonar
                    </Button>
                    {isAdmin && (
                    <Button size="sm" variant="outline" className="text-violet-600 border-violet-200" onClick={() => openFormatosDialog(plantilla)} data-testid={`formato-btn-${plantilla.id}`}>
                      <Layers className="w-4 h-4 mr-1" />
                      Formato
                    </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeletePlantilla(plantilla)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {plantillas.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay plantillas creadas</p>
              {canEdit && (
                <Button className="mt-4" onClick={() => { resetForm(); setShowNewDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Primera Plantilla
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Formatos (subplantillas) Dialog */}
      <Dialog open={showFormatosDialog} onOpenChange={setShowFormatosDialog}>
        <DialogContent className="max-w-xl" data-testid="formatos-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-violet-600" />
              Formatos de "{formatosPlantilla?.nombre}"
            </DialogTitle>
            <DialogDescription>
              Crea subplantillas (formatos) dentro de esta plantilla maestra. Al llenar el checklist de un cliente podrás elegir cuál formato aplicar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!(formatosPlantilla?.tipo_cobertura_opciones?.length) && (
              <div className="p-3 rounded-lg border border-violet-200 bg-violet-50 text-sm text-violet-800" data-testid="formatos-info-maestro">
                Esta plantilla aún no tiene formatos. Al crear el primero, se creará automáticamente la subplantilla
                maestra <strong>"{formatosPlantilla?.nombre}"</strong> con los {formatosPlantilla?.items?.length || 0} items actuales,
                para que siempre puedas elegir el formato original.
              </div>
            )}

            {(formatosPlantilla?.tipo_cobertura_opciones || []).length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Formatos existentes</Label>
                {(formatosPlantilla?.tipo_cobertura_opciones || []).map((f) => {
                  const count = (formatosPlantilla?.items || []).filter((i) => i.tipo_cobertura === f).length;
                  const esMaestro = f === (formatosPlantilla?.formato_maestro || formatosPlantilla?.nombre);
                  return (
                    <div key={f} className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-white" data-testid={`formato-row-${f}`}>
                      {renamingFormato?.anterior === f ? (
                        <>
                          <Input
                            value={renamingFormato.nombre}
                            onChange={(e) => setRenamingFormato({ ...renamingFormato, nombre: e.target.value })}
                            className="h-8 text-sm flex-1"
                            data-testid={`formato-rename-input-${f}`}
                          />
                          <Button size="sm" className="h-8" onClick={handleRenombrarFormato} disabled={savingFormato} data-testid={`formato-rename-save-${f}`}>
                            <Save className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8" onClick={() => setRenamingFormato(null)}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Layers className="w-4 h-4 text-violet-500 shrink-0" />
                          <span className="text-sm font-medium flex-1 truncate">{f}</span>
                          {esMaestro && <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px]">Maestro</Badge>}
                          <Badge variant="secondary" className="bg-slate-100 text-slate-600 text-[10px]">{count} items</Badge>
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Renombrar" onClick={() => setRenamingFormato({ anterior: f, nombre: f })} data-testid={`formato-rename-btn-${f}`}>
                            <Edit className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Eliminar" onClick={() => handleEliminarFormato(f)} disabled={savingFormato} data-testid={`formato-delete-btn-${f}`}>
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div>
              <Label className="text-xs text-slate-500">Nuevo formato</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newFormato}
                  onChange={(e) => setNewFormato(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCrearFormato()}
                  placeholder="Ej: Formato Americano"
                  data-testid="formato-nuevo-input"
                />
                <Button onClick={handleCrearFormato} disabled={savingFormato} data-testid="formato-crear-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Crear Formato
                </Button>
              </div>
              {(formatosPlantilla?.tipo_cobertura_opciones || []).length > 0 && (
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Los items nuevos se clasifican por formato en "Ver Items". Un formato solo puede eliminarse si no tiene items asignados.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFormatosDialog(false)} data-testid="formatos-cerrar-btn">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Plantilla Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nueva Plantilla de Checklist</DialogTitle>
            <DialogDescription>Crea una plantilla de evaluación y asígnale uno o varios ramos</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre de la Plantilla *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Checklist Vehículo de Motor"
                />
              </div>
              <div>
                <Label>Ramos * (puede seleccionar varios)</Label>
                <div className="mt-1 max-h-44 overflow-y-auto border rounded-md p-2 space-y-0.5" data-testid="nueva-plantilla-ramos-multiselect">
                  {ramosDisponibles.ramos_sin_plantilla.map((ramo) => (
                    <label key={ramo} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 rounded px-1 py-0.5">
                      <input
                        type="checkbox"
                        checked={formData.ramos.includes(ramo)}
                        onChange={() => toggleRamoForm(ramo)}
                        data-testid={`ramo-checkbox-${ramo}`}
                      />
                      <span>{ramo}</span>
                    </label>
                  ))}
                  {ramosDisponibles.ramos_sin_plantilla.length === 0 && (
                    <p className="text-xs text-slate-400 p-1">No hay ramos disponibles</p>
                  )}
                </div>
                {formData.ramos.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="ramos-seleccionados-count">{formData.ramos.length} ramo(s) seleccionado(s)</p>
                )}
              </div>
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción de la plantilla..."
                rows={2}
              />
            </div>
            <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                <strong>Nota:</strong> Después de crear la plantilla, podrás agregar los items de evaluación desde la opción "Ver Items".
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreatePlantilla}>Crear Plantilla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Clonar plantilla */}
      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Clonar Plantilla</DialogTitle>
            <DialogDescription>
              Se copiarán los {cloneSource?.items?.length || 0} items de "{cloneSource?.nombre}" a la nueva plantilla; solo debe elegir el ramo y ajustar lo necesario.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nombre de la nueva plantilla *</Label>
              <Input
                value={cloneForm.nombre}
                onChange={(e) => setCloneForm({ ...cloneForm, nombre: e.target.value })}
                data-testid="clonar-nombre-input"
              />
            </div>
            <div>
              <Label>Ramo de la nueva plantilla *</Label>
              <Select value={cloneForm.ramo} onValueChange={(v) => setCloneForm({ ...cloneForm, ramo: v })}>
                <SelectTrigger data-testid="clonar-ramo-select">
                  <SelectValue placeholder="Seleccionar ramo..." />
                </SelectTrigger>
                <SelectContent>
                  {ramosDisponibles.ramos_sin_plantilla.map((ramo) => (
                    <SelectItem key={ramo} value={ramo}>{ramo}</SelectItem>
                  ))}
                  {ramosDisponibles.ramos_sin_plantilla.length === 0 && (
                    <SelectItem value="OTRO" disabled>No hay ramos disponibles</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={cloneForm.descripcion}
                onChange={(e) => setCloneForm({ ...cloneForm, descripcion: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloneDialog(false)}>Cancelar</Button>
            <Button onClick={handleClonarPlantilla} data-testid="clonar-confirmar-btn">
              <Copy className="w-4 h-4 mr-1" />
              Clonar Plantilla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Plantilla Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Plantilla</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre de la Plantilla</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              />
            </div>
            <div>
              <Label>Ramos * (puede seleccionar varios)</Label>
              <div className="mt-1 max-h-44 overflow-y-auto border rounded-md p-2 space-y-0.5" data-testid="editar-plantilla-ramos-multiselect">
                {[...new Set([...(ramosDePlantilla(selectedPlantilla)), ...ramosDisponibles.ramos_sin_plantilla])].map((ramo) => (
                  <label key={ramo} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 rounded px-1 py-0.5">
                    <input
                      type="checkbox"
                      checked={formData.ramos.includes(ramo)}
                      onChange={() => toggleRamoForm(ramo)}
                      data-testid={`editar-ramo-checkbox-${ramo}`}
                    />
                    <span>{ramo}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">La misma plantilla puede aplicar a varios ramos</p>
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdatePlantilla}>Guardar Cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Items Dialog */}
      <Dialog open={showItemsDialog} onOpenChange={handleItemsDialogChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Items de Evaluación - {selectedPlantilla?.nombre}</DialogTitle>
            <DialogDescription>
              Ramo: {selectedPlantilla?.ramo} | {draftItems.length} items
              {canEdit && " — Los cambios se guardan todos juntos con el botón Guardar Cambios"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Add new item form */}
            {canEdit && (
              <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
                <CardContent className="py-4">
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[200px]">
                      <Label className="text-xs">Texto del Item</Label>
                      <Input
                        value={newItem.texto}
                        onChange={(e) => setNewItem({ ...newItem, texto: e.target.value })}
                        placeholder="Ej: Daños a la Propiedad Ajena"
                      />
                    </div>
                    <div className="w-40">
                      <Label className="text-xs">Categoría</Label>
                      <Select value={newItem.categoria} onValueChange={(v) => setNewItem({ ...newItem, categoria: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          {categorias.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Tipo de Cobertura/Formato - para ramos que lo requieren */}
                    {selectedPlantilla?.tiene_tipo_cobertura && (
                      <div className="w-48">
                        <Label className="text-xs text-blue-700">{getTipoCoberturaLabel(selectedPlantilla)} *</Label>
                        <Select value={newItem.tipo_cobertura} onValueChange={(v) => setNewItem({ ...newItem, tipo_cobertura: v })}>
                          <SelectTrigger className="border-blue-300">
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(selectedPlantilla?.tipo_cobertura_opciones || getTipoCoberturaOpciones(selectedPlantilla?.ramo)).map((tipo) => (
                              <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <Button onClick={handleAddItem}>
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                  </div>
                  {selectedPlantilla?.tiene_tipo_cobertura && (
                    <p className="text-xs text-blue-600 mt-2">
                      * Este ramo requiere clasificar cada item como: {(selectedPlantilla?.tipo_cobertura_opciones || getTipoCoberturaOpciones(selectedPlantilla?.ramo)).join(" o ")}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Items list - Drag and drop enabled */}
            <div className="space-y-4">
              {canEdit && draftItems.length > 1 && (
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <GripVertical className="w-3 h-3" />
                  Arrastra los items para reordenarlos
                </p>
              )}
              
              <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext 
                  items={draftItems.map(item => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {Object.entries(groupItemsByCategory(draftItems)).map(([categoria, items]) => (
                    <div key={categoria}>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="bg-slate-100">{categoria}</Badge>
                        <span className="text-xs text-slate-500">{items.length} items</span>
                      </div>
                      <div className="space-y-2 pl-2 border-l-2 border-slate-200">
                        {items.map((item, idx) => (
                          <SortableItem
                            key={item.id}
                            item={item}
                            idx={idx}
                            editingItemId={editingItemId}
                            setEditingItemId={setEditingItemId}
                            handleUpdateItem={handleUpdateItem}
                            handleDeleteItem={handleDeleteItem}
                            canEdit={canEdit}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </SortableContext>
              </DndContext>

              {draftItems.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <CheckSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No hay items en esta plantilla</p>
                  {canEdit && <p className="text-sm">Usa el formulario arriba para agregar items</p>}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="items-center gap-2">
            {canEdit && itemsDirty && (
              <div className="flex items-center gap-2 mr-auto flex-1 min-w-0">
                <span className="text-xs text-amber-600 whitespace-nowrap" data-testid="items-cambios-pendientes">
                  Hay cambios sin guardar
                </span>
                <Input
                  value={notaVersion}
                  onChange={(e) => setNotaVersion(e.target.value)}
                  placeholder="Nota de versión (opcional)"
                  className="h-8 text-xs max-w-xs"
                  data-testid="input-nota-version"
                />
              </div>
            )}
            <Button variant="outline" onClick={() => handleItemsDialogChange(false)} data-testid="items-cancelar-btn">
              {canEdit ? "Cancelar" : "Cerrar"}
            </Button>
            {canEdit && (
              <Button onClick={handleGuardarCambiosItems} disabled={savingItems || !itemsDirty} data-testid="guardar-cambios-items-btn">
                <Save className="w-4 h-4 mr-1" />
                {savingItems ? "Guardando..." : "Guardar Cambios"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog - Admin only */}
      <Dialog open={showHistorialDialog} onOpenChange={setShowHistorialDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              Historial de Versiones
            </DialogTitle>
            <DialogDescription>
              {selectedPlantilla?.nombre} - {selectedPlantilla?.ramo} (Actual: V {selectedPlantilla?.version || "1.0"})
            </DialogDescription>
          </DialogHeader>
          {historialLoading ? (
            <div className="text-center py-8 text-slate-500">Cargando historial...</div>
          ) : (historialVersiones.length > 0 || historialActual) ? (
            <>
              <p className="text-xs text-slate-500 -mt-1">Marca dos versiones y pulsa "Comparar" para ver las diferencias lado a lado.</p>
              <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-1">
                {[...(historialActual ? [historialActual] : []), ...historialVersiones].map((ver) => (
                  <div key={ver.id} className="border rounded-lg p-4 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid={`version-${ver.es_actual ? "actual" : ver.version}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300"
                          checked={compareSelection.includes(ver.id)}
                          onChange={() => toggleCompareSelection(ver.id)}
                          data-testid={`compare-check-${ver.es_actual ? "actual" : ver.version}`}
                        />
                        <Badge variant="secondary" className="font-mono">V {ver.version}</Badge>
                        {ver.es_actual && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">Actual</Badge>}
                        <span className="text-sm text-slate-600">{ver.items?.length || 0} items</span>
                      </div>
                      {!ver.es_actual && (
                        <Button size="sm" variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => { if (window.confirm(`¿Restaurar la versión V ${ver.version}? Esto creará una nueva versión con el contenido anterior.`)) handleRestoreVersion(ver.id); }} data-testid={`restore-btn-${ver.version}`}>
                          Restaurar
                        </Button>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 space-y-1">
                      {ver.saved_at && <p>Guardada: {new Date(ver.saved_at).toLocaleString('es-DO')}</p>}
                      {!ver.es_actual && <p>Por: {ver.saved_by_name || "Sistema"}</p>}
                      {ver.nota_auto && <p className="text-sky-600" data-testid={`nota-auto-${ver.es_actual ? "actual" : ver.version}`}>Cambios: {ver.nota_auto}</p>}
                      {ver.version_nota && <p className="italic text-slate-600" data-testid={`nota-manual-${ver.es_actual ? "actual" : ver.version}`}>Nota: "{ver.version_nota}"</p>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <p>No hay versiones anteriores registradas</p>
              <p className="text-xs mt-1">El historial se genera cuando se modifica la plantilla</p>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={handleCompararVersiones}
              disabled={compareSelection.length !== 2 || comparing}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="comparar-versiones-btn"
            >
              {comparing ? "Comparando..." : `Comparar (${compareSelection.length}/2)`}
            </Button>
            <Button variant="outline" onClick={() => setShowHistorialDialog(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comparación de Versiones Dialog */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-y-auto" data-testid="compare-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              Comparación de Versiones
            </DialogTitle>
            {compareData && (
              <DialogDescription>
                V {compareData.base?.version} → V {compareData.nueva?.version}{compareData.nueva?.es_actual ? " (Actual)" : ""} · {compareData.resumen}
              </DialogDescription>
            )}
          </DialogHeader>
          {compareData && (() => {
            const keyOf = (i) => i?.id || `txt::${(i?.texto || "").trim().toLowerCase()}`;
            const addedKeys = new Set((compareData.diff?.agregadas || []).map(keyOf));
            const removedKeys = new Set((compareData.diff?.eliminadas || []).map(keyOf));
            const modNuevaKeys = new Set((compareData.diff?.modificadas || []).map(m => keyOf(m.despues)));
            const modBaseKeys = new Set((compareData.diff?.modificadas || []).map(m => keyOf(m.antes)));
            const itemClass = (base) =>
              base === "added" ? "bg-emerald-50 border-emerald-300 text-emerald-900" :
              base === "removed" ? "bg-red-50 border-red-300 text-red-900 line-through" :
              base === "modified" ? "bg-amber-50 border-amber-300 text-amber-900" :
              "bg-white dark:bg-slate-900 border-slate-200";
            const renderCol = (ver, isBase) => (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="secondary" className="font-mono">V {ver?.version}</Badge>
                  {ver?.es_actual && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">Actual</Badge>}
                  <span className="text-xs text-slate-500">{ver?.items?.length || 0} items</span>
                </div>
                {ver?.saved_at && <p className="text-[11px] text-slate-500 mb-2">{new Date(ver.saved_at).toLocaleString('es-DO')} {ver.saved_by_name ? `· ${ver.saved_by_name}` : ""}</p>}
                <div className="space-y-1.5">
                  {(ver?.items || []).map((it, idx) => {
                    const k = keyOf(it);
                    const estado = isBase
                      ? (removedKeys.has(k) ? "removed" : modBaseKeys.has(k) ? "modified" : "same")
                      : (addedKeys.has(k) ? "added" : modNuevaKeys.has(k) ? "modified" : "same");
                    return (
                      <div key={`${k}-${idx}`} className={`border rounded px-2 py-1.5 text-xs ${itemClass(estado)}`}>
                        <span className="font-medium">{it.texto}</span>
                        {it.categoria && <span className="text-[10px] opacity-70 ml-1">[{it.categoria}]</span>}
                        {it.tipo_cobertura && <span className="text-[10px] opacity-70 ml-1">({Array.isArray(it.tipo_cobertura) ? it.tipo_cobertura.join(", ") : it.tipo_cobertura})</span>}
                        {it.observacion && <p className="text-[10px] opacity-70 mt-0.5">{it.observacion}</p>}
                      </div>
                    );
                  })}
                  {(ver?.items || []).length === 0 && <p className="text-xs text-slate-400 italic">Sin coberturas</p>}
                </div>
              </div>
            );
            return (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-3 text-[11px]">
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-200 border border-emerald-400"></span> Agregada ({compareData.diff?.agregadas?.length || 0})</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-200 border border-red-400"></span> Eliminada ({compareData.diff?.eliminadas?.length || 0})</span>
                  <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-200 border border-amber-400"></span> Modificada ({compareData.diff?.modificadas?.length || 0})</span>
                </div>
                <div className="flex gap-4">
                  {renderCol(compareData.base, true)}
                  <div className="w-px bg-slate-200 dark:bg-slate-700"></div>
                  {renderCol(compareData.nueva, false)}
                </div>
                {(compareData.diff?.modificadas || []).length > 0 && (
                  <div className="border-t pt-3">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Detalle de modificaciones</p>
                    <div className="space-y-1.5">
                      {compareData.diff.modificadas.map((m, i) => (
                        <div key={i} className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                          <span className="line-through text-red-700">{m.antes?.texto}{m.antes?.observacion ? ` — ${m.antes.observacion}` : ""}</span>
                          <span className="mx-1">→</span>
                          <span className="text-emerald-700 font-medium">{m.despues?.texto}{m.despues?.observacion ? ` — ${m.despues.observacion}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompareDialog(false)} data-testid="compare-cerrar-btn">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Papelera de Plantillas (30 días) */}
      <Dialog open={showPapeleraDialog} onOpenChange={setShowPapeleraDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="papelera-plantillas-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-slate-500" />
              Papelera de Plantillas
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Las plantillas eliminadas se guardan 30 días. Pasado ese tiempo se borran definitivamente.</p>
          {loadingPapelera ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : papelera.length === 0 ? (
            <div className="text-center py-10 text-slate-400" data-testid="papelera-vacia">
              <Trash2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">La papelera está vacía</p>
            </div>
          ) : (
            <div className="space-y-2">
              {papelera.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700" data-testid={`papelera-item-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 dark:text-white truncate">{p.nombre}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700">{p.ramo}</Badge>
                      <span className="text-xs text-slate-500">
                        Eliminada {p.eliminada_at ? new Date(p.eliminada_at).toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                        {p.eliminada_por ? ` por ${p.eliminada_por}` : ""}
                      </span>
                      <Badge variant="secondary" className={`text-xs ${p.dias_restantes <= 5 ? "bg-red-100 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                        {p.dias_restantes} día{p.dias_restantes === 1 ? "" : "s"} restante{p.dias_restantes === 1 ? "" : "s"}
                      </Badge>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 shrink-0" onClick={() => handleRestaurarPapelera(p)} data-testid={`restaurar-papelera-${p.id}`}>
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Restaurar
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPapeleraDialog(false)} data-testid="papelera-cerrar-btn">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
